import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Target, ArrowLeft, Clock, Rocket, TrendingUp, FileText, AlertTriangle, Info, Users, Search, Mail, Linkedin, CheckCircle, XCircle } from "lucide-react";
import HiringManagerSearch from "@/components/HiringManagerSearch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
export default function FindHiringManager() {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-blue-50/50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/jobintel-360-reports')} className="h-9 w-9 p-0 bg-white">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <Target className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Hiring Manager ID</h1>
              <p className="text-muted-foreground mt-1">
                Skip the ATS and connect directly with decision-makers!
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/hiring-manager-reports')} className="gap-2 bg-white">
            <FileText className="h-4 w-4" />
            My Reports
          </Button>
        </div>

        {/* Hero Card */}
        <Card className="border-2 border-blue-400 bg-blue-50 shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Get Your Resume Directly to Decision-Makers
              </h2>
              <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed px-[8px]">
                Most job seekers apply online and wait. The problem is that online applications alone 
                generate just a <span className="text-destructive font-bold">2% success rate</span>. 
                Our tool gives you a <span className="text-primary font-bold">97% chance</span> of finding 
                the exact person who can say "yes" to your application—giving you a{" "}
                <span className="font-bold">10-20x higher response rate</span>.
              </p>
            </div>

            {/* Two Info Links */}
            <div className="flex items-center justify-center gap-6 mt-4 mb-8">
              {/* Link 1: Why you need this (Amber themed) */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-amber-600 hover:text-amber-700 underline decoration-amber-400 underline-offset-4 font-semibold transition-colors">
                    <AlertTriangle className="w-4 h-4 mr-1.5" />
                    Why you need this
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="w-5 h-5" />
                      Why You Need Direct Access to Hiring Managers
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-6">
                      {/* Section 1: The Online Application Trap */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <h3 className="font-semibold text-amber-800 mb-2">The Online Application Trap</h3>
                        <p className="text-sm text-amber-900/80 mb-3">
                          Most job seekers apply online, upload their resume, and wait. The problem is that 
                          online applications alone only generate about a <span className="font-bold text-destructive">2% success rate</span> for 
                          landing interviews.
                        </p>
                        <div className="bg-white rounded-lg p-3 border border-amber-200">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-destructive">98%</div>
                            <div className="text-sm text-muted-foreground">of resumes never seen by a decision-maker</div>
                          </div>
                        </div>
                      </div>

                      {/* Section 2: Why Hiring Managers Matter */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">Why Hiring Managers Matter</h3>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">They make the final hiring decision—not HR screeners</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Bypass recruiters who filter based on keywords</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Stand out from 250+ other applicants</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 3: The Statistics */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">The Hard Truth: Job Search Statistics</h3>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left p-3 font-medium">Metric</th>
                                <th className="text-right p-3 font-medium">Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-t">
                                <td className="p-3">Average applications per job</td>
                                <td className="p-3 text-right font-semibold">250+</td>
                              </tr>
                              <tr className="border-t bg-red-50">
                                <td className="p-3">Online application success rate</td>
                                <td className="p-3 text-right font-semibold text-destructive">2%</td>
                              </tr>
                              <tr className="border-t">
                                <td className="p-3">Interview invitations per posting</td>
                                <td className="p-3 text-right font-semibold">4-6</td>
                              </tr>
                              <tr className="border-t bg-green-50">
                                <td className="p-3">Response increase with direct outreach</td>
                                <td className="p-3 text-right font-semibold text-success">10-20x</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 4: Why Most Job Seekers Fail */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">Why Most Job Seekers Can't Find Hiring Managers</h3>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Company websites don't list decision-makers for roles</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">LinkedIn filters are limited and often inaccurate</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Org charts are hidden or outdated</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-muted-foreground">Result: hours wasted guessing and messaging wrong people</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>

              <span className="text-muted-foreground">|</span>

              {/* Link 2: How we find them (Blue themed) */}
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-blue-600 hover:text-blue-700 underline decoration-blue-400 underline-offset-4 font-semibold transition-colors">
                    <Info className="w-4 h-4 mr-1.5" />
                    How we find them →
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-blue-600">
                      <Search className="w-5 h-5" />
                      How We Find Your Hiring Manager
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-6">
                      {/* Section 1: Our Success Rate */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="font-semibold text-blue-800 mb-2">Our 97% Success Rate</h3>
                        <p className="text-sm text-blue-900/80 mb-3">
                          Our tool searches the internet like a mad man, scouring <span className="font-bold">600+ online resources</span> to find your hiring manager. We achieve a 97% success rate through our multi-source approach:
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-blue-900/80">Cross-reference multiple data sources simultaneously</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-blue-900/80">AI-powered name and title matching algorithms</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-blue-900/80">Email pattern detection and verification</p>
                          </div>
                        </div>
                      </div>

                      {/* Section 2: What We Search */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">What We Search</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                            <Linkedin className="w-4 h-4 text-blue-600" />
                            <span className="text-sm">LinkedIn company pages</span>
                          </div>
                          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                            <Users className="w-4 h-4 text-blue-600" />
                            <span className="text-sm">Employee lists & org charts</span>
                          </div>
                          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm">Job posting metadata</span>
                          </div>
                          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                            <Search className="w-4 h-4 text-blue-600" />
                            <span className="text-sm">Press releases & news</span>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 3: What You Get */}
                      <div>
                        <h3 className="font-semibold text-foreground mb-3">What You Get</h3>
                        <div className="space-y-3">
                          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <Users className="w-5 h-5 text-success mt-0.5" />
                            <div>
                              <p className="font-medium text-sm">Hiring Manager Details</p>
                              <p className="text-xs text-muted-foreground">Full name, job title, and department context</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <Linkedin className="w-5 h-5 text-success mt-0.5" />
                            <div>
                              <p className="font-medium text-sm">LinkedIn Profile</p>
                              <p className="text-xs text-muted-foreground">Direct link to their professional profile</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <Mail className="w-5 h-5 text-success mt-0.5" />
                            <div>
                              <p className="font-medium text-sm">Email Pattern</p>
                              <p className="text-xs text-muted-foreground">Likely email format (e.g., firstname.lastname@company.com)</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <Target className="w-5 h-5 text-success mt-0.5" />
                            <div>
                              <p className="font-medium text-sm">Personalized Outreach Strategy</p>
                              <p className="text-xs text-muted-foreground">How to approach them effectively</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 4: The Other 3% */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <h3 className="font-semibold text-amber-800 mb-2">The Other 3%: Still Valuable Intel</h3>
                        <p className="text-sm text-amber-900/80 mb-3">
                          When we can't find the exact hiring manager, we still provide:
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-amber-900/80">Probable title of the decision-maker</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-amber-900/80">Related department contacts</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-amber-900/80">Step-by-step manual search strategy</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-amber-900/80">Best guesses ranked by confidence level</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>

            {/* 4 Feature Boxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 text-center border border-blue-200 shadow-sm">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Rocket className="w-5 h-5 text-blue-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">Skip the ATS</div>
                <div className="text-xs text-muted-foreground">Bypass automated filters</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-blue-200 shadow-sm">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">97% Success</div>
                <div className="text-xs text-muted-foreground">Find exact decision-makers</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-blue-200 shadow-sm">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">10-20x Response</div>
                <div className="text-xs text-muted-foreground">vs. online applications</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-blue-200 shadow-sm">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div className="font-semibold text-sm text-foreground">~5 Minutes</div>
                <div className="text-xs text-muted-foreground">Fast results</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Section */}
        <HiringManagerSearch />
      </div>
    </div>;
}